import React, { useEffect, useState, useRef } from 'react';

interface Spark {
  id: number;
  angle: number;
  speed: number;
  size: number;
  color: string;
  createdAt: number;
}

interface SparksProps {
  count?: number;
  colors?: string[];
  duration?: number;
  distance?: number;
  sizeRange?: [number, number];
  sparkRate?: number;
}

export const Sparks: React.FC<SparksProps> = ({
  count = 150,
  colors = ['#FFFFFF'],
  duration = 15,
  distance = 1500,
  sizeRange = [40, 10],
  sparkRate = 300
}) => {
  const [sparks, setSparks] = useState<Spark[]>([]);
  const sparkIdRef = useRef(0);

  const createSpark = (): Spark => {
    sparkIdRef.current += 1;
    return {
      id: sparkIdRef.current,
      angle: Math.random() * Math.PI * 2,
      speed: 0.5 + Math.random() * 1,
      size: sizeRange[0] + Math.random() * (sizeRange[1] - sizeRange[0]),
      color: colors[Math.floor(Math.random() * colors.length)],
      createdAt: Date.now()
    };
  };

  useEffect(() => {
    setSparks([createSpark()]);

    const interval = setInterval(() => {
      setSparks(prevSparks => {
        const now = Date.now();
        const activeSparks = prevSparks.filter(
          spark => now - spark.createdAt < duration * 1000
        );
        
        if (activeSparks.length < count) {
          return [...activeSparks, createSpark()];
        }
        
        return activeSparks;
      });
    }, sparkRate);

    return () => clearInterval(interval);
  }, [count, duration, sparkRate]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden opacity-50" >
      {sparks.map((spark) => (
        <div
          key={spark.id}
          className="absolute rounded-full"
          style={{
            left: '50%',
            top: '50%',
            width: `${spark.size}px`,
            height: `${spark.size}px`,
            backgroundColor: spark.color,
            boxShadow: `
              0 0 ${spark.size * 0.5}px ${spark.color},
              0 0 ${spark.size}px ${spark.color},
              0 0 ${spark.size * 1.5}px ${spark.color}80
            `,
            filter: 'blur(3px)',
            animation: `sparkFly ${duration}s ease-out forwards`,
            '--angle': `${spark.angle}rad`,
            '--speed': spark.speed,
            '--distance': `${distance}px`
          } as React.CSSProperties}
        />
      ))}
      
      <style>{`
        @keyframes sparkFly {
          0% {
            transform: translate(-50%, -50%) scale(0) rotate(0deg);
            opacity: 1;
          }
          5% {
            transform: translate(-50%, -50%) scale(1.2) rotate(90deg);
            opacity: 1;
          }
          10% {
            transform: translate(-50%, -50%) scale(1) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: translate(
              calc(-50% + calc(var(--speed) * var(--distance) * cos(var(--angle)))),
              calc(-50% + calc(var(--speed) * var(--distance) * sin(var(--angle))))
            ) scale(0.8) rotate(720deg);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}; 